import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowDown, ArrowRight } from "lucide-react";
import { useContent } from "@/context/ContentContext";

const lineVariants = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 1.1, delay: 0.4 + i * 0.15, ease: [0.16, 1, 0.3, 1] },
  }),
};

const CTA = ({ link, className, children }) => {
  if (link && link.startsWith("/")) return <Link to={link} className={className}>{children}</Link>;
  return <a href={link || "#"} className={className}>{children}</a>;
};

export const Hero = () => {
  const h = useContent().hero;
  const lines = [h.line1, h.line2].filter(Boolean);

  return (
    <section id="top" data-testid="hero-section" className="relative min-h-screen flex flex-col justify-center overflow-hidden bg-ink">
      <motion.div
        initial={{ scale: 1.15, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 2, ease: "easeOut" }}
        className="absolute inset-0"
      >
        <img src={h.bg_image} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink/90 via-ink/50 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-b from-ink/20 via-transparent to-ink" />
        <div className="absolute inset-0" style={{ background: "radial-gradient(60% 50% at 30% 45%, rgba(58,231,225,0.12), transparent 70%)" }} />
        <div className="scanline absolute inset-x-0 top-0 h-px bg-gold/20" style={{ boxShadow: "0 0 10px rgba(58,231,225,0.45)" }} />
      </motion.div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 w-full">
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3, duration: 1 }} className="font-mono text-xs tracking-[0.3em] uppercase text-gold text-glow mb-8">
          {h.eyebrow}
        </motion.p>

        <h1 className="font-jp text-6xl sm:text-7xl lg:text-8xl font-black tracking-tight leading-[0.95] text-white drop-glow">
          {lines.map((line, i) => (
            <span key={i} className="line-mask">
              <motion.span custom={i} variants={lineVariants} initial="hidden" animate="visible" className="inline-block chrome-text">
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 1 }} className="mt-10 max-w-xl text-base sm:text-lg font-light leading-relaxed text-white/70">
          {h.subtitle}
        </motion.p>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.3, duration: 1 }} className="mt-12 flex flex-wrap items-center gap-5">
          <CTA link={h.cta1_link} className="group inline-flex items-center gap-3 rounded-full bg-gold px-7 py-3.5 text-sm font-medium text-ink hover:bg-gold-light glow-cyan transition-colors duration-300">
            {h.cta1_label}
            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
          </CTA>
          <CTA link={h.cta2_link} className="inline-flex items-center rounded-full border border-white/20 px-7 py-3.5 text-sm text-white hover:border-gold/70 hover:text-gold transition-colors duration-300">
            {h.cta2_label}
          </CTA>
        </motion.div>
      </div>
    </section>
  );
};
